import React, { useCallback, useMemo, useState } from "react";
import {
  FileDown,
  FileSpreadsheet,
  ListOrdered,
  Loader2,
  Tag,
} from "lucide-react";
import { toast } from "react-toastify";
import ReactSelect, { SingleValue, StylesConfig } from "react-select";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns-tz";

import PageHeader from "../../../components/PageHeader";
import { SELECT_STYLES } from "../../../components/DateFilters/constants";
import { BrazilDate } from "../../../utils/BrazilDate";
import { getErrorMessage } from "../../../utils/errorHandler";
import RelatorioPeriodoMaxHint from "../components/RelatorioPeriodoMaxHint";
import RelatorioQueryErrorAlert from "../components/RelatorioQueryErrorAlert";
import { warnIfReportPeriodInvalid } from "../utils/reportPeriodValidation";
import { lojaService } from "../../../services/lojaService";
import { fetchRelatorioPromocoesPizzas } from "../../../services/pedidoIntegracaoService";
import {
  exportRelatorioPromocoesPizzaPdf,
  exportRelatorioPromocoesPizzaXlsx,
  type RelatorioPromocoesFiltroResumo,
} from "../utils/relatorioPromocoesPizzaExport";

type LojaOpt = { value: number; label: string };

const LOJA_SELECT_STYLES = SELECT_STYLES as unknown as StylesConfig<
  LojaOpt,
  false
>;

function getDefaultMonthRange() {
  const now = new Date();
  const tz = BrazilDate.getTimezone();
  const yearStr = format(now, "yyyy", { timeZone: tz });
  const monthStr = format(now, "MM", { timeZone: tz });
  const year = parseInt(yearStr, 10);
  const month = parseInt(monthStr, 10);
  const firstDay = BrazilDate.create(year, month, 1);
  const nextMonth = month === 12 ? 1 : month + 1;
  const nextYear = month === 12 ? year + 1 : year;
  const firstDayNextMonth = BrazilDate.create(nextYear, nextMonth, 1);
  const lastDay = new Date(firstDayNextMonth.getTime() - 24 * 60 * 60 * 1000);
  return {
    dataInicio: BrazilDate.formatISO(firstDay),
    dataFim: BrazilDate.formatISO(lastDay),
  };
}

const DEFAULT_MONTH = getDefaultMonthRange();

function formatQty(n: number): string {
  return n.toLocaleString("pt-BR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

function qtyPorTamanho(
  porTamanho: { tamanhoNome: string; quantidade: number }[] | undefined,
  kind: "08" | "broto" | "trem"
): number {
  if (!porTamanho?.length) return 0;
  return porTamanho.reduce((sum, t) => {
    const nome = t.tamanhoNome.toLowerCase();
    if (kind === "broto") {
      return /broto/i.test(nome) ? sum + t.quantidade : sum;
    }
    if (kind === "trem") {
      return /trem/i.test(nome) ? sum + t.quantidade : sum;
    }
    // 08 pedaços (evita pegar broto / trem / 04)
    const is08 =
      /\b08\b/.test(nome) ||
      /\b8\s*peda/.test(nome) ||
      /pizza\s*08/.test(nome) ||
      /08\s*peda/.test(nome);
    const isBroto = /broto/i.test(nome);
    const isTrem = /trem/i.test(nome);
    if (is08 && !isBroto && !isTrem) return sum + t.quantidade;
    return sum;
  }, 0);
}

const PizzasPromocoes: React.FC = () => {
  const [startDate, setStartDate] = useState(DEFAULT_MONTH.dataInicio);
  const [endDate, setEndDate] = useState(DEFAULT_MONTH.dataFim);
  const [lojaId, setLojaId] = useState<number | null>(null);
  const [applied, setApplied] = useState<null | {
    dataInicio: string;
    dataFim: string;
    lojaId: number | null;
  }>(null);
  const [promoLinePages, setPromoLinePages] = useState<Record<string, number>>(
    {}
  );
  const [mostrarPromocoesSemVenda, setMostrarPromocoesSemVenda] =
    useState(false);

  const { data: lojas = [] } = useQuery({
    queryKey: ["lojas"],
    queryFn: () => lojaService.getLojas(),
  });

  const lojaOptions = useMemo(
    () => lojas.map((l) => ({ value: l.id, label: l.nome })),
    [lojas]
  );
  const lojaSel = lojaOptions.find((o) => o.value === (lojaId ?? -1)) ?? null;

  const { data, isFetching, isError, error, refetch } = useQuery({
    queryKey: [
      "relatorio-promocoes-pizzas",
      applied?.dataInicio ?? "no-applied",
      applied?.dataFim ?? "no-applied",
      applied?.lojaId ?? "no-applied",
    ],
    enabled: applied != null,
    queryFn: () =>
      fetchRelatorioPromocoesPizzas({
        dataInicio: applied!.dataInicio,
        dataFim: applied!.dataFim,
        lojaId: applied!.lojaId,
      }),
  });

  const handleAplicar = () => {
    if (warnIfReportPeriodInvalid(startDate, endDate)) return;

    setApplied({
      dataInicio: startDate,
      dataFim: endDate,
      lojaId,
    });
  };

  const queryErrorMessage = isError ? getErrorMessage(error) : null;

  const pIni =
    applied != null
      ? BrazilDate.formatBR(BrazilDate.fromISOString(applied.dataInicio), false)
      : null;
  const pFim =
    applied != null
      ? BrazilDate.formatBR(BrazilDate.fromISOString(applied.dataFim), false)
      : null;
  const lojaLabel =
    applied != null
      ? applied.lojaId != null
        ? (lojaOptions.find((o) => o.value === applied.lojaId)?.label ?? "—")
        : "Todas"
      : null;

  React.useEffect(() => {
    // Se a query mudar (novo filtro), reseta a paginação interna de linhas.
    setPromoLinePages({});
  }, [applied?.dataInicio, applied?.dataFim, applied?.lojaId]);

  const totalPizzas = useMemo(
    () =>
      (data?.resumoPorPromocao ?? []).reduce(
        (s, r) => s + r.quantidadePizzas,
        0
      ),
    [data?.resumoPorPromocao]
  );

  const resumoPorPromocaoOrdenado = useMemo(() => {
    const lista = [...(data?.resumoPorPromocao ?? [])].sort((a, b) => {
      if (b.quantidadePizzas !== a.quantidadePizzas) {
        return b.quantidadePizzas - a.quantidadePizzas;
      }
      return a.promocaoLabel.localeCompare(b.promocaoLabel, "pt-BR");
    });
    if (mostrarPromocoesSemVenda) return lista;
    return lista.filter((r) => r.quantidadePizzas > 0);
  }, [data?.resumoPorPromocao, mostrarPromocoesSemVenda]);

  const qtdPromocoesSemVenda = useMemo(
    () =>
      (data?.resumoPorPromocao ?? []).filter((r) => r.quantidadePizzas <= 0)
        .length,
    [data?.resumoPorPromocao]
  );

  const gruposTabelaLojaTamanhoSabor = useMemo(() => {
    const rows = data?.agregadoLojaTamanhoSabor ?? [];
    const ordem: string[] = [];
    const porKey = new Map<string, typeof rows>();
    const resumoPorKey = new Map(
      (data?.resumoPorPromocao ?? []).map((r) => [r.promocaoKey, r])
    );
    for (const r of rows) {
      if (!porKey.has(r.promocaoKey)) {
        ordem.push(r.promocaoKey);
        porKey.set(r.promocaoKey, []);
      }
      porKey.get(r.promocaoKey)!.push(r);
    }
    return ordem.map((k) => {
      const linhas = porKey.get(k)!;
      return {
        promocaoKey: k,
        promocaoLabel: linhas[0]?.promocaoLabel ?? k,
        resumo: resumoPorKey.get(k),
        linhas,
      };
    });
  }, [data?.agregadoLojaTamanhoSabor, data?.resumoPorPromocao]);

  const linhasExport = useMemo(() => {
    const rows = data?.agregadoLojaTamanhoSabor ?? [];
    return rows.map((r) => ({
      promocaoLabel: r.promocaoLabel,
      lojaNome: r.lojaNome,
      tamanhoNome: r.tamanhoNome,
      saborNome: r.saborNome,
      quantidade: r.quantidade,
    }));
  }, [data?.agregadoLojaTamanhoSabor]);

  const totalPorTamanho = useMemo(() => {
    const rows = data?.agregadoLojaTamanhoSabor ?? [];
    const map = new Map<string, number>();
    for (const row of rows) {
      const tamanho = row.tamanhoNome || "(sem tamanho)";
      map.set(tamanho, (map.get(tamanho) ?? 0) + row.quantidade);
    }
    return Array.from(map.entries())
      .map(([tamanhoNome, quantidade]) => ({ tamanhoNome, quantidade }))
      .sort((a, b) => {
        if (b.quantidade !== a.quantidade) return b.quantidade - a.quantidade;
        return a.tamanhoNome.localeCompare(b.tamanhoNome, "pt-BR");
      });
  }, [data?.agregadoLojaTamanhoSabor]);

  const totalQuantidadeExport = useMemo(
    () => linhasExport.reduce((s, r) => s + r.quantidade, 0),
    [linhasExport]
  );

  const filtrosExport = useMemo((): RelatorioPromocoesFiltroResumo[] => {
    return [{ label: "Loja", value: lojaLabel ?? "—" }];
  }, [lojaLabel]);

  const buildExportInput = useCallback(() => {
    if (!applied) return null;
    return {
      periodoInicioISO: applied.dataInicio,
      periodoFimISO: applied.dataFim,
      filtros: filtrosExport,
      linhas: linhasExport,
      totalQuantidadeFracao: totalQuantidadeExport,
      geradoEmLabel: BrazilDate.formatBR(new Date(), true),
    };
  }, [applied, filtrosExport, linhasExport, totalQuantidadeExport]);

  const handleExportPdf = useCallback(() => {
    if (!applied) {
      toast.warning("Aplique os filtros antes de exportar.");
      return;
    }
    if (linhasExport.length === 0) {
      toast.warning("Não há linhas para exportar.");
      return;
    }
    try {
      const input = buildExportInput();
      if (!input) {
        toast.warning("Aplique os filtros antes de exportar.");
        return;
      }
      exportRelatorioPromocoesPizzaPdf(input);
      toast.success("PDF baixado.");
    } catch {
      toast.error("Não foi possível gerar o PDF.");
    }
  }, [applied, linhasExport.length, buildExportInput]);

  const handleExportExcel = useCallback(() => {
    if (!applied) {
      toast.warning("Aplique os filtros antes de exportar.");
      return;
    }
    if (linhasExport.length === 0) {
      toast.warning("Não há linhas para exportar.");
      return;
    }
    try {
      const input = buildExportInput();
      if (!input) {
        toast.warning("Aplique os filtros antes de exportar.");
        return;
      }
      exportRelatorioPromocoesPizzaXlsx(input);
      toast.success("Planilha Excel baixada.");
    } catch {
      toast.error("Não foi possível gerar o Excel.");
    }
  }, [applied, linhasExport.length, buildExportInput]);

  const PAGE_SIZE = 20;

  return (
    <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
      <PageHeader
        title="Pizzas em promoções"
        description=""
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={handleExportExcel}
              disabled={!applied || linhasExport.length === 0 || isFetching}
              className="inline-flex items-center gap-2 rounded-lg border border-emerald-600 bg-white px-3 py-2 text-sm font-semibold text-emerald-800 shadow-sm transition-opacity hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <FileSpreadsheet size={18} />
              Excel
            </button>
            <button
              type="button"
              onClick={handleExportPdf}
              disabled={!applied || linhasExport.length === 0 || isFetching}
              className="inline-flex items-center gap-2 rounded-lg bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <FileDown size={18} />
              PDF (A4)
            </button>
          </div>
        }
      />

      <div className="mb-6 rounded-lg border border-neutral-200 bg-neutral-50/80 p-4">
        <p className="mb-3 text-sm font-medium text-neutral-800">Filtros</p>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <div className="flex flex-col gap-1">
            <label
              htmlFor="promo-ini"
              className="text-xs font-medium text-neutral-700"
            >
              Data início
            </label>
            <input
              id="promo-ini"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:border-transparent focus:ring-2 focus:ring-primary"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label
              htmlFor="promo-fim"
              className="text-xs font-medium text-neutral-700"
            >
              Data fim
            </label>
            <input
              id="promo-fim"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:border-transparent focus:ring-2 focus:ring-primary"
            />
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-xs font-medium text-neutral-700">Loja</span>
            <ReactSelect<LojaOpt, false>
              inputId="promo-loja"
              value={lojaSel}
              onChange={(o: SingleValue<LojaOpt>) =>
                setLojaId(o?.value ?? null)
              }
              options={lojaOptions}
              isClearable
              placeholder="Todas"
              styles={LOJA_SELECT_STYLES}
              className="text-sm"
              noOptionsMessage={() => "Nenhuma loja"}
            />
          </div>
        </div>
        <RelatorioPeriodoMaxHint />
        <button
          type="button"
          onClick={handleAplicar}
          className="mt-4 inline-flex rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white shadow-sm hover:opacity-90"
        >
          Aplicar filtros
        </button>
      </div>

      {applied != null ? (
        <p className="mb-4 text-sm text-neutral-600">
          Período: <span className="font-medium text-neutral-800">{pIni}</span>{" "}
          a <span className="font-medium text-neutral-800">{pFim}</span>
          {" · "}
          Loja:{" "}
          <span className="font-medium text-neutral-800">{lojaLabel}</span>
          {totalPizzas > 0 && (
            <>
              {" · "}
              Total de pizzas (promoções):{" "}
              <span className="font-medium text-neutral-800">
                {formatQty(totalPizzas)}
              </span>
            </>
          )}
        </p>
      ) : (
        <p className="mb-4 text-sm text-neutral-600">
          Selecione o período e clique em <strong>Aplicar filtros</strong>.
        </p>
      )}

      <div className="relative">
        {applied != null && isFetching && (
          <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-white/60">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        )}

        {applied != null && queryErrorMessage ? (
          <RelatorioQueryErrorAlert message={queryErrorMessage} />
        ) : null}

        {applied != null && !queryErrorMessage ? (
          <section className="mb-8" aria-label="Resumo por promoção">
            {totalPorTamanho.length > 0 && (
              <div className="mb-4 rounded-lg border border-neutral-200 bg-white p-4 shadow-sm">
                <h3 className="mb-2 text-sm font-semibold text-neutral-900">
                  Total de pizzas produzidas por tamanho
                </h3>
                <div className="flex flex-wrap gap-2">
                  {totalPorTamanho.map((item) => (
                    <span
                      key={item.tamanhoNome}
                      className="inline-flex items-center gap-2 rounded-full border border-neutral-200 bg-neutral-50 px-3 py-1 text-xs text-neutral-800"
                    >
                      <span className="font-medium">{item.tamanhoNome}</span>
                      <span className="font-semibold tabular-nums">
                        {formatQty(item.quantidade)}
                      </span>
                    </span>
                  ))}
                </div>
              </div>
            )}
            <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <h2 className="flex items-center gap-2 text-base font-semibold text-neutral-900">
                <Tag className="h-5 w-5 text-primary" strokeWidth={2} />
                Resumo por promoção
              </h2>
              <button
                type="button"
                onClick={() =>
                  setMostrarPromocoesSemVenda((prev) => !prev)
                }
                className={`inline-flex items-center justify-center rounded-md border px-3 py-1.5 text-xs font-medium transition ${
                  mostrarPromocoesSemVenda
                    ? "border-primary bg-primary/10 text-primary"
                    : "border-neutral-300 bg-white text-neutral-700 hover:bg-neutral-50"
                }`}
                aria-pressed={mostrarPromocoesSemVenda}
              >
                {mostrarPromocoesSemVenda
                  ? "Ocultar promoções sem venda"
                  : `Exibir promoções sem venda${
                      qtdPromocoesSemVenda > 0
                        ? ` (${qtdPromocoesSemVenda})`
                        : ""
                    }`}
              </button>
            </div>
            <div className="overflow-x-auto rounded-lg border border-neutral-200 bg-white shadow-sm">
              <table className="min-w-full text-left text-sm">
                <thead className="border-b border-neutral-200 bg-neutral-50">
                  <tr>
                    <th className="px-4 py-3 font-semibold text-neutral-800">
                      Promoção
                    </th>
                    <th className="px-4 py-3 font-semibold text-neutral-800">
                      Qtd. vendida
                    </th>
                    <th className="px-4 py-3 font-semibold text-neutral-800">
                      08 Pedaços
                    </th>
                    <th className="px-4 py-3 font-semibold text-neutral-800">
                      Broto
                    </th>
                    <th className="px-4 py-3 font-semibold text-neutral-800">
                      Trem
                    </th>
                    <th className="px-4 py-3 font-semibold text-neutral-800">
                      Total pizzas
                    </th>
                    <th className="px-4 py-3 font-semibold text-neutral-800">
                      Sabores distintos
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {resumoPorPromocaoOrdenado.length === 0 ? (
                    <tr>
                      <td
                        colSpan={7}
                        className="px-4 py-8 text-center text-sm text-neutral-500"
                      >
                        Nenhuma promoção com venda no período.
                      </td>
                    </tr>
                  ) : (
                    resumoPorPromocaoOrdenado.map((r) => (
                      <tr
                        key={r.promocaoKey}
                        className={`border-b border-neutral-100 last:border-0 ${
                          r.quantidadePizzas <= 0
                            ? "bg-neutral-50/80 text-neutral-500"
                            : ""
                        }`}
                      >
                        <td className="px-4 py-2.5 text-neutral-900">
                          {r.promocaoLabel}
                        </td>
                        <td className="px-4 py-2.5 tabular-nums text-neutral-800">
                          {formatQty(r.quantidadeItens ?? r.quantidadePizzas)}
                        </td>
                        <td className="px-4 py-2.5 tabular-nums text-neutral-800">
                          {formatQty(qtyPorTamanho(r.porTamanho, "08"))}
                        </td>
                        <td className="px-4 py-2.5 tabular-nums text-neutral-800">
                          {formatQty(qtyPorTamanho(r.porTamanho, "broto"))}
                        </td>
                        <td className="px-4 py-2.5 tabular-nums text-neutral-800">
                          {formatQty(qtyPorTamanho(r.porTamanho, "trem"))}
                        </td>
                        <td className="px-4 py-2.5 font-medium tabular-nums text-neutral-900">
                          {formatQty(r.quantidadePizzas)}
                        </td>
                        <td className="px-4 py-2.5 tabular-nums text-neutral-800">
                          {r.quantidadeSaboresDistintos}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
              <p className="border-t border-neutral-100 px-4 py-2.5 text-xs text-neutral-500">
                <span className="font-medium text-neutral-700">Qtd. vendida</span>{" "}
                = itens/combos do cardápio.{" "}
                <span className="font-medium text-neutral-700">
                  08 / Broto / Trem
                </span>{" "}
                = pizzas daquele tamanho geradas pela promoção.{" "}
                <span className="font-medium text-neutral-700">Total pizzas</span>{" "}
                = soma de todos os tamanhos.
              </p>
            </div>
          </section>
        ) : null}

        {applied != null && !queryErrorMessage ? (
          <section aria-label="Detalhe por promoção" className="mt-10">
            <div className="mb-4 flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <ListOrdered size={22} strokeWidth={2} />
              </div>
              <div className="min-w-0">
                <h2 className="text-base font-semibold text-neutral-900">
                  Totais por loja, tamanho e sabor (por promoção)
                </h2>
                <p className="mt-1 text-sm text-neutral-600">
                  Mesmo critério do relatório «Pizzas por sabor»: inteira em 1,
                  meia em 0,5, etc. Dentro de cada promoção, linhas por{" "}
                  <strong className="font-medium text-neutral-800">
                    quantidade decrescente
                  </strong>{" "}
                  (o que mais vendeu aparece primeiro).
                </p>
              </div>
            </div>

            {totalPizzas === 0 ? (
              <div className="rounded-lg border border-neutral-200 bg-white px-4 py-8 text-center text-sm text-neutral-500 shadow-sm">
                Nenhuma pizza em promoção no período.
              </div>
            ) : (
              <div className="space-y-10">
                {gruposTabelaLojaTamanhoSabor.map(
                  ({ promocaoKey, promocaoLabel, resumo, linhas }) => {
                    if (linhas.length === 0) return null;

                    const totalFracionadoGrupo = linhas.reduce(
                      (s, r) => s + r.quantidade,
                      0
                    );

                    const page = promoLinePages[promocaoKey] ?? 0;
                    const totalPages = Math.max(
                      1,
                      Math.ceil(linhas.length / PAGE_SIZE)
                    );
                    const start = page * PAGE_SIZE;
                    const end = start + PAGE_SIZE;
                    const linhasPage = linhas.slice(start, end);

                    return (
                      <div
                        key={promocaoKey}
                        className="rounded-2xl border border-neutral-200/90 bg-white p-4 shadow-sm sm:p-6"
                      >
                        <h3 className="border-b border-neutral-200 pb-2 text-lg font-semibold text-neutral-900">
                          {promocaoLabel}
                        </h3>
                        {resumo ? (
                          <p className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-sm text-neutral-700">
                            <span>
                              <span className="text-neutral-500">
                                Itens/combos vendidos:{" "}
                              </span>
                              <span className="font-semibold tabular-nums text-neutral-900">
                                {formatQty(
                                  resumo.quantidadeItens ??
                                    resumo.quantidadePizzas
                                )}
                              </span>
                            </span>
                            <span>
                              <span className="text-neutral-500">
                                08 Pedaços:{" "}
                              </span>
                              <span className="font-semibold tabular-nums text-neutral-900">
                                {formatQty(qtyPorTamanho(resumo.porTamanho, "08"))}
                              </span>
                            </span>
                            <span>
                              <span className="text-neutral-500">Broto: </span>
                              <span className="font-semibold tabular-nums text-neutral-900">
                                {formatQty(
                                  qtyPorTamanho(resumo.porTamanho, "broto")
                                )}
                              </span>
                            </span>
                            <span>
                              <span className="text-neutral-500">Trem: </span>
                              <span className="font-semibold tabular-nums text-neutral-900">
                                {formatQty(
                                  qtyPorTamanho(resumo.porTamanho, "trem")
                                )}
                              </span>
                            </span>
                            <span>
                              <span className="text-neutral-500">
                                Total pizzas:{" "}
                              </span>
                              <span className="font-semibold tabular-nums text-neutral-900">
                                {formatQty(resumo.quantidadePizzas)}
                              </span>
                            </span>
                            <span>
                              <span className="text-neutral-500">
                                Sabores distintos:{" "}
                              </span>
                              <span className="font-semibold tabular-nums text-neutral-900">
                                {resumo.quantidadeSaboresDistintos}
                              </span>
                            </span>
                            <span>
                              <span className="text-neutral-500">
                                Σ frações na tabela abaixo:{" "}
                              </span>
                              <span className="font-semibold tabular-nums text-neutral-900">
                                {formatQty(totalFracionadoGrupo)}
                              </span>
                            </span>
                          </p>
                        ) : null}

                        <div className="mt-4 overflow-x-auto rounded-xl border border-neutral-100">
                          <table className="w-full min-w-[520px] text-left text-sm">
                            <thead>
                              <tr className="border-b border-neutral-200 bg-neutral-50/90">
                                <th className="px-3 py-2.5 font-semibold text-neutral-800 sm:px-4">
                                  Loja
                                </th>
                                <th className="px-3 py-2.5 font-semibold text-neutral-800 sm:px-4">
                                  Tamanho
                                </th>
                                <th className="px-3 py-2.5 font-semibold text-neutral-800 sm:px-4">
                                  Sabor
                                </th>
                                <th className="px-3 py-2.5 text-right font-semibold text-neutral-800 sm:px-4">
                                  Quantidade
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {linhasPage.map((row, idx) => (
                                <tr
                                  key={`${row.lojaNome}-${row.tamanhoNome}-${row.saborNome}-${start + idx}`}
                                  className="border-b border-neutral-100 last:border-b-0"
                                >
                                  <td className="px-3 py-2.5 text-neutral-900 sm:px-4">
                                    {row.lojaNome}
                                  </td>
                                  <td className="px-3 py-2.5 text-neutral-900 sm:px-4">
                                    {row.tamanhoNome}
                                  </td>
                                  <td className="px-3 py-2.5 text-neutral-900 sm:px-4">
                                    {row.saborNome}
                                  </td>
                                  <td className="px-3 py-2.5 text-right font-medium tabular-nums text-neutral-900 sm:px-4">
                                    {formatQty(row.quantidade)}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                            <tfoot className="border-t-2 border-neutral-200 bg-neutral-50/90">
                              <tr>
                                <td
                                  colSpan={3}
                                  className="px-3 py-2.5 text-sm font-semibold text-neutral-800 sm:px-4"
                                >
                                  Total (fracionado)
                                </td>
                                <td className="px-3 py-2.5 text-right text-sm font-bold tabular-nums text-neutral-900 sm:px-4">
                                  {formatQty(totalFracionadoGrupo)}
                                </td>
                              </tr>
                            </tfoot>
                          </table>
                        </div>

                        {totalPages > 1 ? (
                          <div className="mt-4 flex items-center justify-between gap-3">
                            <button
                              type="button"
                              onClick={() =>
                                setPromoLinePages((prev) => ({
                                  ...prev,
                                  [promocaoKey]: Math.max(0, page - 1),
                                }))
                              }
                              disabled={page <= 0}
                              className="rounded-lg border border-neutral-200 bg-white px-3 py-1.5 text-sm font-medium text-neutral-800 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              Anterior
                            </button>
                            <span className="text-sm font-medium tabular-nums text-neutral-700">
                              Página {page + 1} / {totalPages}
                            </span>
                            <button
                              type="button"
                              onClick={() =>
                                setPromoLinePages((prev) => ({
                                  ...prev,
                                  [promocaoKey]: Math.min(
                                    totalPages - 1,
                                    page + 1
                                  ),
                                }))
                              }
                              disabled={page >= totalPages - 1}
                              className="rounded-lg border border-neutral-200 bg-white px-3 py-1.5 text-sm font-medium text-neutral-800 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              Próxima
                            </button>
                          </div>
                        ) : null}
                      </div>
                    );
                  }
                )}
              </div>
            )}
          </section>
        ) : null}

        {applied != null ? (
          <button
            type="button"
            onClick={() => void refetch()}
            className="mt-6 text-sm font-medium text-primary hover:underline"
          >
            Recarregar dados
          </button>
        ) : null}
      </div>
    </main>
  );
};

export default PizzasPromocoes;
